import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Check, LoaderCircle } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Residential Roofing",
  "Roof Repair",
  "Roof Replacement",
  "Siding Installation / Replacement",
  "Gutter Installation / Replacement",
  "Storm Damage Restoration",
  "Hail / Wind Damage Inspection",
  "Insurance Restoration",
  "Emergency Roof Repair",
  "Other",
];

const inputClass =
  "w-full border border-white/15 bg-transparent px-4 py-3.5 text-sm text-white placeholder-white/35 outline-none transition-[border-color] duration-300 focus:border-gold";

export default function LeadForm() {
  const [requestType, setRequestType] = useState("inspection");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    zip_code: "",
    service: SERVICE_OPTIONS[0],
    message: "",
  });
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    const handler = (e) => setRequestType(e.detail);
    window.addEventListener("seraph:lead-type", handler);
    return () => window.removeEventListener("seraph:lead-type", handler);
  }, []);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/leads`, { ...form, request_type: requestType });
      setStatus("success");
      toast.success("Request received — we'll be in touch shortly.");
    } catch (err) {
      setStatus("idle");
      toast.error("Something went wrong. Please call us at (317) 516-9317.");
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative border-t border-white/10 py-28 md:py-40"
    >
      <div className="mx-auto grid max-w-7xl gap-16 px-6 md:px-10 lg:grid-cols-2 lg:gap-12">
        <Reveal>
          <Eyebrow>Free Inspection &amp; Free Quote</Eyebrow>
          <h2 className="mt-6 font-serif text-5xl font-medium leading-[1.02] tracking-tight md:text-6xl">
            Let's Protect
            <br />
            <span className="italic text-gold">What Matters Most.</span>
          </h2>
          <p className="mt-8 max-w-md text-sm leading-relaxed text-white/60 md:text-base">
            Tell us about your home and we'll schedule your free roof, siding, or gutter
            inspection — or put together a free, no-pressure quote. Honest answers, quality
            work, every time.
          </p>
          <div className="mt-12 space-y-5">
            <a
              data-testid="contact-phone-link"
              href="tel:+13175169317"
              className="group flex items-center gap-5"
            >
              <span className="flex h-12 w-12 items-center justify-center border border-white/15 transition-colors duration-300 group-hover:border-gold">
                <Phone className="h-5 w-5 text-gold" />
              </span>
              <span>
                <span className="block text-[10px] font-bold uppercase tracking-[0.3em] text-white/40">
                  Call or Text
                </span>
                <span className="mt-1 block font-serif text-2xl text-white transition-colors duration-300 group-hover:text-gold">
                  (317) 516-9317
                </span>
              </span>
            </a>
            <a
              data-testid="contact-email-link"
              href="mailto:seraphroofing@gmail.com"
              className="group flex items-center gap-5"
            >
              <span className="flex h-12 w-12 items-center justify-center border border-white/15 transition-colors duration-300 group-hover:border-gold">
                <Mail className="h-5 w-5 text-gold" />
              </span>
              <span>
                <span className="block text-[10px] font-bold uppercase tracking-[0.3em] text-white/40">
                  Email
                </span>
                <span className="mt-1 block font-serif text-2xl text-white transition-colors duration-300 group-hover:text-gold">
                  seraphroofing@gmail.com
                </span>
              </span>
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="border border-white/10 bg-onyx/70 p-8 backdrop-blur-sm md:p-10">
            {status === "success" ? (
              <div data-testid="lead-success" className="flex flex-col items-start gap-6 py-10">
                <span className="flex h-14 w-14 items-center justify-center border border-gold/50 bg-gold/10">
                  <Check className="h-6 w-6 text-gold" />
                </span>
                <h3 className="font-serif text-4xl font-medium tracking-tight">
                  Request received.
                </h3>
                <p className="text-sm leading-relaxed text-white/60">
                  Thank you for reaching out. A member of our team will contact you shortly to
                  schedule your free {requestType === "inspection" ? "inspection" : "quote"}.
                  Need us sooner? Call <a href="tel:+13175169317" className="text-gold">(317) 516-9317</a>.
                </p>
                <button
                  data-testid="lead-another-btn"
                  onClick={() => {
                    setStatus("idle");
                    setForm({ name: "", email: "", phone: "", address: "", city: "", zip_code: "", service: SERVICE_OPTIONS[0], message: "" });
                  }}
                  className="border border-white/20 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.25em] text-white/70 transition-colors duration-300 hover:border-gold hover:text-gold"
                >
                  Send Another Request
                </button>
              </div>
            ) : (
              <form data-testid="lead-form" onSubmit={submit}>
                <div className="mb-8 grid grid-cols-2 gap-px border border-white/15 bg-white/15">
                  <button
                    type="button"
                    data-testid="lead-tab-inspection"
                    onClick={() => setRequestType("inspection")}
                    className={`px-4 py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] transition-colors duration-300 ${
                      requestType === "inspection"
                        ? "bg-gold text-ink"
                        : "bg-onyx text-white/55 hover:text-white"
                    }`}
                  >
                    Free Inspection
                  </button>
                  <button
                    type="button"
                    data-testid="lead-tab-quote"
                    onClick={() => setRequestType("quote")}
                    className={`px-4 py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] transition-colors duration-300 ${
                      requestType === "quote"
                        ? "bg-gold text-ink"
                        : "bg-onyx text-white/55 hover:text-white"
                    }`}
                  >
                    Free Quote
                  </button>
                </div>

                <div className="space-y-5">
                  <input
                    data-testid="lead-name-input"
                    required
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Full name"
                    className={inputClass}
                  />
                  <div className="grid gap-5 sm:grid-cols-2">
                    <input
                      data-testid="lead-email-input"
                      required
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="Email"
                      className={inputClass}
                    />
                    <input
                      data-testid="lead-phone-input"
                      required
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="Phone"
                      className={inputClass}
                    />
                  </div>
                  <input
                    data-testid="lead-address-input"
                    required
                    value={form.address}
                    onChange={update("address")}
                    placeholder="Street address"
                    className={inputClass}
                  />
                  <div className="grid gap-5 sm:grid-cols-2">
                    <input
                      data-testid="lead-city-input"
                      required
                      value={form.city}
                      onChange={update("city")}
                      placeholder="City"
                      className={inputClass}
                    />
                    <input
                      data-testid="lead-zip-input"
                      required
                      value={form.zip_code}
                      onChange={update("zip_code")}
                      placeholder="ZIP code"
                      className={inputClass}
                    />
                  </div>
                  <select
                    data-testid="lead-service-select"
                    value={form.service}
                    onChange={update("service")}
                    className={`${inputClass} appearance-none bg-onyx`}
                  >
                    {SERVICE_OPTIONS.map((option) => (
                      <option key={option} value={option} className="bg-onyx">
                        {option}
                      </option>
                    ))}
                  </select>
                  <textarea
                    data-testid="lead-message-input"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us about your project or storm damage (optional)"
                    rows={4}
                    className={`${inputClass} resize-none`}
                  />
                </div>

                <button
                  data-testid="lead-submit-btn"
                  type="submit"
                  disabled={status === "sending"}
                  className="mt-8 flex w-full items-center justify-center gap-3 bg-gold px-8 py-4 text-xs font-bold uppercase tracking-[0.25em] text-ink transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-gold-light disabled:opacity-60"
                >
                  {status === "sending" ? (
                    <>
                      <LoaderCircle className="h-4 w-4 animate-spin" /> Sending
                    </>
                  ) : requestType === "inspection" ? (
                    "Get My Free Inspection"
                  ) : (
                    "Get My Free Quote"
                  )}
                </button>
                <p className="mt-4 text-center text-[11px] leading-relaxed text-white/35">
                  No pressure. No obligation. Just honest answers from a team you can trust.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
